import { DB_FIELD_MAX_LENGTH } from "@selector/shared/settings";
import type { FC } from "react";
import { reduxForm, SubmissionError } from "redux-form";
import { Dialog, TriggerButton } from "widgets";

import type { BrowsingDirectoryFragment } from "@/modules/files/graphql/files-queries.gql.ts";

import { fileNameValidation, simpleFieldValidation } from "../../../app-utils/form.ts";
import { createProjectFolder, validateFileNames } from "../../../redux/modules/files.ts";
import { type ReduxDispatch } from "../../../redux/redux-utils.ts";
import {
  type IPopoverPropsInjectedFromController,
  PopoverController,
} from "../../../ui-modules/core/overlays/components/OverlayControllers.tsx";
import InputField, { InputReduxFormField } from "../../../ui-modules/core/redux-form/InputField.tsx";

interface CreateFolderActionProps extends CreateFolderDialogProps {
  isDisabled?: boolean;
  isOffscreen?: boolean;
  showLabel?: boolean;
}

enum FieldName {
  name = "name",
}

const CreateFolderHeaderAction: FC<CreateFolderActionProps> = (props) => {
  return (
    <PopoverController
      isOffscreen={props.isOffscreen}
      withForm
      actionWidget={(controllerProps) => (
        <TriggerButton
          {...controllerProps}
          isDisabled={!props.currentFolder?.isEditable ? "This folder is not editable" : undefined}
          icon={"create_new_folder"}
          tooltip={"Create a new folder in the current location"}
          label={"New Folder"}
          showLabel={props.showLabel ?? true}
        />
      )}
    >
      {(controllerProps) => (
        <CreateFolderFormDialog {...controllerProps} currentFolder={props.currentFolder} onSaved={props.onSaved} />
      )}
    </PopoverController>
  );
};

interface CreateFolderDialogProps {
  onSaved?: (response: boolean) => void;
  currentFolder: BrowsingDirectoryFragment;
}

const CreateFolderFormDialog = reduxForm<FieldName, CreateFolderDialogProps & IPopoverPropsInjectedFromController>({
  form: "createFolder",
  asyncValidate,
  asyncBlurFields: [],
  touchOnBlur: false,
})((props) => {
  return (
    <Dialog
      {...props}
      ref={props.dialogRef}
      mainButton={{
        label: "Create Folder",
        isLoading: props.submitting || !!props.asyncValidating,
        onPress: props.handleSubmit(handleSubmitData),
      }}
      width={350}
      aria-label={"Create a new folder"}
      errorCallout={{ message: props.error }}
    >
      <InputReduxFormField
        componentClass="input"
        name={FieldName.name}
        label={"New folder name"}
        maxLength={DB_FIELD_MAX_LENGTH.RESOURCE.NAME}
        component={InputField}
        validate={[simpleFieldValidation, fileNameValidation]}
      />
    </Dialog>
  );
});

function handleSubmitData(
  values,
  dispatch: ReduxDispatch,
  props: CreateFolderDialogProps & IPopoverPropsInjectedFromController,
) {
  const name: string = values[FieldName.name]?.trim();

  return dispatch(createProjectFolder(props.currentFolder.id, name))
    .then((response) => {
      props.onSaved?.(response);
      props.closeFn();
    })
    .catch(async (error) => {
      throw new SubmissionError({
        _error: error.message,
      });
    });
}

// Check for duplicates
async function asyncValidate(
  values,
  dispatch: ReduxDispatch,
  props: CreateFolderActionProps & IPopoverPropsInjectedFromController,
) {
  const name: string = values[FieldName.name]?.trim();

  // We don't need to give the full path as validateFileNames pull it from redux
  const existingFiles: string[] = await dispatch(validateFileNames(props.currentFolder.id, [{ name } as File])).catch(
    (error) => {
      throw {
        [FieldName.name]: error.message,
      };
    },
  );

  // if the return array is empty == no duplicates
  if (existingFiles.length > 0) {
    throw {
      [FieldName.name]: `The folder "${name}" already exists.`,
    };
  }

  // return undefined (default) validates the current fields (values)
}

export { CreateFolderHeaderAction };
